import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import {
  handleUpdateDraftItem,
  handleUpdateDraftItemBanner,
  handleAddBannerToDraftItem,
  deleteBannerItem,
} from "store/cms/footer/partners/slice";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Button from "components/buttons/Button";
import styles from "../CmsFooterPartnersEditModal.module.scss";
import { CmsFooterPartnersBannerCard } from "../components/CmsFooterPartnersCard";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import { getGeoDropdownOptions } from "helpers/geoDropdown";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { useEffect } from "react";

type Props = {
  newUploadedImages: React.MutableRefObject<{ id: number; image: File }[]>;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  resetErrors?: () => void;
  readOnly?: boolean;
};

const PartnersMainTab: React.FC<Props> = ({
  newUploadedImages,
  fieldErrors,
  clearFieldError,
  resetErrors,
  readOnly
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const geoList = useAppSelector(selectFilteredGeo);
  const draftItem = useAppSelector((store) => store["cms-partners"].draftItem);
  const bannerIdToDelete = useAppSelector(
    (store) => store["cms-partners"].processindBannerId,
  );
  const globalFileError = fieldErrors?.["files"];

  useEffect(() => {
    resetErrors?.();
  }, [draftItem.geo]);

  const handleUpdateField = (key: string, value: any) => {
    dispatch(handleUpdateDraftItem({ [key]: value }));
  };

  const handleAddNewBanner = () => {
    dispatch(handleAddBannerToDraftItem());
  };

  const handleDeleteBanner = async (id: number) => {
    newUploadedImages.current = newUploadedImages.current.filter(
      (i) => i.id !== id,
    );

    dispatch(deleteBannerItem({ partnerId: +draftItem.id!, bannerId: id }))
      .unwrap()
      .then(() => {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityDeleteSuccess"),
            type: NotificationTypes.success,
          }),
        );
      })
      .catch(() => {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityDeleteError"),
            type: NotificationTypes.error,
          }),
        );
      });
  };

  const handleUpdateBanner = ({
    key,
    value,
    id,
    order,
  }: {
    key: string;
    value: any;
    id: number;
    order: number;
  }) => {
    if (key === "image" && value instanceof File) {
      newUploadedImages.current = [
        ...newUploadedImages.current.filter((i) => i.id !== id),
        { id, image: value },
      ];
      return;
    }

    dispatch(
      handleUpdateDraftItemBanner({
        id,
        [key]: value,
        priority: order,
      }),
    );
  };

  const getBannerErrors = (index: number) => {
    if (!fieldErrors) return {};

    const prefix = `items.${index}.`;

    return Object.keys(fieldErrors)
      .filter((key) => key.startsWith(prefix))
      .reduce<Record<string, string>>((acc, key) => {
        const fieldName = key.replace(prefix, "");
        acc[fieldName] = fieldErrors[key];
        return acc;
      }, {});
  };

  return (
    <div className={styles.cmsFooterPartnersContent}>
      <div className={styles.cmsFooterPartnersContentUpper}>
        <InnerInput
          value={draftItem?.title ?? ""}
          placeholder={t("enterTitle")}
          label={t("title")}
          type="text"
          id="pages-title"
          onChange={(e) => {
            handleUpdateField("title", e);
            clearFieldError("title");
          }}
          hasError={Boolean(fieldErrors?.["title"])}
          disabled={readOnly}
        />
        <DefaultDropdown
          value={draftItem?.geo || "WW"}
          placeholder={t("geo")}
          label={t("geo")}
          options={getGeoDropdownOptions(geoList)}
          onSelect={(v) => {
            handleUpdateField("geo", v);
            clearFieldError("geo");
          }}
          hasError={Boolean(fieldErrors?.["geo"])}
          disabled={readOnly}
        />
        <SwitchBtn
          activeTxt={t("active")}
          // notActiveTxt={t("hidden")}
          isActive={!!draftItem?.isActive}
          handleClick={() =>
            handleUpdateField("isActive", !draftItem?.isActive)
          }
          disabled={readOnly}
        />
      </div>

      <div className={styles.cmsFooterPartnersContentLower}>
        {draftItem.items.map((item, idx) => {
          const bannerErrors = getBannerErrors(idx);

          return (
            <CmsFooterPartnersBannerCard
              key={item.id}
              id={item.id}
              alt={item.alt ?? ""}
              link={item.href ?? ""}
              image={item.imageHref || ""}
              isActive={!!draftItem.isActive}
              fieldErrors={{
                ...bannerErrors,
                ...(globalFileError ? { files: globalFileError } : {}),
              }}
              clearFieldError={(field) =>
                clearFieldError(`items.${idx}.${field}`)
              }
              onChangeAlt={(v) => {
                handleUpdateBanner({
                  key: "alt",
                  value: v,
                  id: item.id,
                  order: idx + 1,
                });
                clearFieldError("alt");
              }}
              onChangeLink={(v) => {
                handleUpdateBanner({
                  key: "href",
                  value: v,
                  id: item.id,
                  order: idx + 1,
                });
                clearFieldError("link");
              }}
              onAddImage={(v) => {
                handleUpdateBanner({
                  key: "image",
                  value: v,
                  id: item.id,
                  order: idx + 1,
                });
                clearFieldError("files");
              }}
              onDeleteBannerCard={() => handleDeleteBanner(item.id)}
              isDeleting={item.id === bannerIdToDelete}
              readOnly={readOnly}
            />
          );
        })}

        {!readOnly && (
          <Button
            handleClick={handleAddNewBanner}
            txt="Add New +"
            className={styles.cmsFooterPartnersContentButton}
          />     
        )}
      </div>
    </div>
  );
};

export default PartnersMainTab;
